<template>
    <v-card>
        <v-card-title class="d-flex flex-column align-start text-subtitle-1 text-sm-h6">
            <span>{{ formTitle }}</span>
            <div v-if="preSelectedClient && preSelectedClient.merkmale && preSelectedClient.merkmale.length > 0" class="d-flex align-center">
                <v-chip v-for="merkmal in preSelectedClient.merkmale" :key="'chip-' + merkmal.id" class="ml-0 font-weight-regular" small>{{ merkmal.merkmal }}</v-chip>
            </div>
        </v-card-title>
        <v-card-text class="pt-3">
            <!-- Client Selection (only show if no pre-selected client) -->
            <v-autocomplete 
                v-if="!preSelectedClient"
                v-model="editedItem.selected_customer" 
                :loading="!loadedCustomersAndAppointments" 
                loader-height="5"
                :disabled="!loadedCustomersAndAppointments" 
                :items="customers" 
                item-text="name" 
                item-value="item_id"
                prepend-inner-icon="mdi-account"
                label="Klient / Institution" 
                outlined dense return-object 
                class="mb-5 font-weight-medium" 
                hint="Für wen erstellst du diese Rechnung?"
                :persistent-hint="editedItem.selected_customer === null"
                :hide-details="editedItem.selected_customer !== null"
                placeholder="Bitte wähle einen Klienten / Institution aus" 
                :persistent-placeholder="editedItem.selected_customer === null"
            >
                <template v-slot:item="{ item }">
                    <div class="d-flex align-center">
                        <div v-if="item.type === 'client'">
                            <span>{{ item.name }}</span>
                            <v-chip v-for="merkmal in item.merkmale" :key="'chip-' + merkmal.id" class="ml-2" small>{{ merkmal.merkmal }}</v-chip>
                        </div>
                        <div v-else-if="item.type === 'institution'">
                            <span>{{ item.name }}</span>
                            <v-chip class="ml-2" small>Institution</v-chip>
                        </div>
                    </div>
                </template>
                <template v-slot:selection="{ item }">
                    <div class="d-flex align-center">
                        <div v-if="item.type === 'client'">
                            <span>{{ item.name }}</span>
                            <v-chip v-for="merkmal in item.merkmale" :key="'chip-selection-' + merkmal.id" class="ml-2 font-weight-regular" small>{{ merkmal.merkmal }}</v-chip>
                        </div>
                        <div v-else-if="item.type === 'institution'">
                            <span>{{ item.name }}</span>
                            <v-chip class="ml-2 font-weight-regular" small>Institution</v-chip>
                        </div>
                    </div>
                </template>
            </v-autocomplete>

            <v-alert v-if="hasSelectedInstitution" dense outlined type="info" prominent class="text-body-2 mb-6 mt-5">
                Rechnungen für Institutionen sind für Fälle gedacht, in denen du eine Sammelrechnung für mehrere Klienten erstellen musst, z.B. bei Zusammenarbeit mit einer <strong>Clearing-Stelle</strong> oder dem Projekt <strong>"Gesund aus der Krise"</strong>. 
                Derzeit können diese Rechnungen nicht als PDF oder Word-Dokument erstellt werden und müssen manuell erstellt werden, falls erforderlich. 
                Sie dienen nur zur korrekten Anzeige in der Übersicht und für die Buchhaltung. 
                Wenn du nur eine Rechnung für <strong>einen Klienten</strong> mit einer Institution als Empfänger erstellen möchtest, wähle bitte den Klienten und nicht die Institution aus.
            </v-alert>

            <!-- Show empty state when client is selected but no appointments available -->
            <v-card v-if="hasSelectedCustomer && !hasAvailableAppointments" outlined class="mt-2">
                <v-card-text class="text-center py-8">
                    <v-icon size="64" color="grey lighten-1" class="mb-4">
                        mdi-calendar-check
                    </v-icon>
                    <div class="text-h6 grey--text text--darken-1 mb-2">
                        Es wurden bereits alle Termine für {{ currentClientName }} verrechnet
                    </div>
                </v-card-text>
            </v-card>

            <!-- Show appointments and invoice sections (always visible, but may be disabled) -->
            <template v-if="!hasSelectedCustomer || hasAvailableAppointments">
                <!-- Appointments Selection Panel -->
                <v-expansion-panels v-model="terminePanel" class="mt-2 flat-expansion-panels" flat>
                    <v-expansion-panel :disabled="!canExpandTermine">
                        <v-expansion-panel-header>
                            <div class="d-flex align-center">
                                <v-icon left>mdi-calendar-check</v-icon>
                                <span class="text-h6">Verrechenbare Termine</span>
                                <v-chip v-if="hasSelectedCustomer && selected_appointments.length > 0" :color="$store.state.theme.primary" dark small class="ml-2">
                                    <v-icon v-if="selected_appointments.length === 0" left small> mdi-cursor-default-click-outline </v-icon>
                                    {{ selected_appointments.length === 0 ? 'Wähle die zu verrechnenden Termine aus' : `${selected_appointments.length} ausgewählt` }}
                                </v-chip>
                                <v-chip v-if="!canExpandTermine" small class="ml-2" color="grey" text-color="white">
                                    Erst nach Kundenauswahl verfügbar
                                </v-chip>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-data-table 
                                v-model="selected_appointments" 
                                :headers="headers_appointments"
                                :items="filteredAppointments" 
                                item-key="id" 
                                show-select 
                                class="clickable-rows" 
                                :items-per-page="-1"
                                hide-default-footer 
                                @click:row="toggleSelection"
                            >
                                <template v-slot:item.datum="{ item }">
                                    <span>
                                        {{ new Date(item.datum).toLocaleString([], {
                                            year: 'numeric', month: '2-digit', day: '2-digit', hour:
                                                '2-digit', minute: '2-digit'
                                        }) }}
                                    </span>
                                    <v-chip v-if="isAfterNow(item.datum)" :color="$store.state.theme.orange" dark small label class="ml-2">
                                        <v-tooltip bottom :color="$store.state.theme.primary">
                                            <template v-slot:activator="{ on }">
                                                <span class="font-weight-medium" v-on="on">Z</span>
                                            </template>
                                            Dieser Termin liegt in der Zukunft.
                                        </v-tooltip>
                                    </v-chip>
                                </template>
                                <template v-slot:item.name="{ item }">
                                    <div>
                                        <div>{{ item.nachname }} {{ item.vorname }}</div>
                                        <div v-if="item.fk_partner_id" class="text-caption">
                                            <v-icon small class="mr-1">mdi-human-male-female</v-icon>
                                            mit {{ item.partner_vorname }} {{ item.partner_nachname }}
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:item.bezeichnung="{ item }">
                                    <div>
                                        <div>{{ item.bezeichnung }}</div>
                                        <div v-if="item.fk_partner_id" class="text-caption">
                                            <v-icon small class="mr-1">mdi-human-male-female</v-icon>
                                            mit {{ item.partner_vorname }} {{ item.partner_nachname }}
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:item.preis="{ item }">
                                    <span v-if="item.dauer">{{ item.preis }}€ ({{ item.dauer }} Minuten)</span>
                                    <span v-else>{{ item.preis }} €</span>
                                </template>
                                <template v-if="hasSelectedCustomer" v-slot:body.append="{ headers }">
                                    <tr class="no-hover">
                                        <td class="py-3" :colspan="headers.length - 1">
                                            <div class="subtitle-1 font-weight-medium black--text">Rechnungsbetrag</div>
                                            <div class="subtitle-2 font-weight-regular">= Summe der ausgewählten Termine</div>
                                        </td>
                                        <td class="py-3">
                                            <div class="text-h5 black--text font-weight-bold">
                                                {{ amountOfSelectedAppointments }} €
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
                
                <v-alert
                    v-if="editedItem.ausweisen_an_beide_klienten && !partnerIdConsistency.isValid"
                    type="error"
                    outlined
                    dense
                    class="mt-3"
                >
                    Wenn die Rechnung an beide Klienten ausgewiesen wird, darf nur ein eindeutiger Partner in den ausgewählten Terminen vorhanden sein. 
                    <strong>{{ currentClientName }}</strong> hatte Termine mit {{ partnerIdConsistency.distinctPartnerNames.join(', ') }}. 
                    Bitte wähle nur Termine mit einem Partner aus, um die Rechnung an beide Klienten auszustellen.
                </v-alert>

                <!-- Invoice Details Panel -->
                <v-expansion-panels v-model="rechnungsdetailsPanel" class="mt-6 flat-expansion-panels" flat>
                <v-expansion-panel :disabled="!canExpandRechnungsdetails">
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon left>mdi-file-document-outline</v-icon>
                            <span class="text-h6">Rechnungsdetails</span>
                            <v-chip v-if="!canExpandRechnungsdetails" small class="ml-2" color="grey" text-color="white">
                                Erst nach Terminauswahl verfügbar
                            </v-chip>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row class="mt-3">
                            <v-col cols="6" class="my-0 py-0">
                                <v-menu v-model="menu" :close-on-content-click="false" offset-y max-width="290px" min-width="auto">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field dense outlined v-model="computedDateFormatted"
                                            label="Rechnungsdatum" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs"
                                            v-on="on" />
                                    </template>
                                    <v-date-picker first-day-of-week="1" v-model="editedItem.datum" no-title @input="menu = false" />
                                </v-menu>                        
                            </v-col>
                            <v-col cols="6" class="my-0 py-0">
                                <v-text-field dense outlined v-model="editedItem.nummer" 
                                    label="Rechnungsnummer" :hint="hintInvoiceNumber" 
                                    :persistent-hint="hasSelectedInstitution" />
                            </v-col>
                        </v-row>

                        <v-alert
                            v-if="hasInvoiceYearMismatch"
                            dense
                            outlined
                            color="orange"
                            type="error"
                            class="text-justify mt-4"
                        >
                            <span class="font-weight-bold">Achtung:</span>
                            Die Rechnungsnummer enthält das Jahr <strong>{{ invoiceYear }}</strong>, das sich vom Jahr des Rechnungsdatums
                            <strong>{{ formatInvoiceDateYear(editedItem.datum) }}</strong> unterscheidet. Bitte prüfe, ob dies korrekt ist.
                            <p class="mt-3">
                                Um die Rechnungsnummer auf das Jahr <strong>{{ formatInvoiceDateYear(editedItem.datum) }}</strong> umzustellen, klicke auf <strong>Umstellen</strong>
                                oder überschreibe die vorgeschlagene Rechnungsnummer.
                                Sollte die Rechnungsnummer {{ editedItem.nummer }} richtig sein, kannst du diese Warnung ignorieren.
                            </p>
                            <v-btn class="mb-3" :color="$store.state.theme.green" outlined @click="updateInvoiceNumberToCurrentYear">
                                Umstellen
                            </v-btn>
                        </v-alert>

                        <!-- Rechnungsempfänger Section -->
                        <div v-if="showInvoiceBothClientsSwitch" class="mt-6">
                            <p class="text-subtitle-1 font-weight-medium mb-4">Rechnungsempfänger</p>
                            <div class="d-flex align-center">
                                <v-checkbox
                                    v-model="editedItem.ausweisen_an_beide_klienten"
                                    :color="$store.state.theme.green"
                                    class="my-2 mr-2 pt-0"
                                    :hide-details="canAddPartnerToBilling"
                                    label="Partner zum Rechnungsempfänger hinzufügen"
                                    hint="Es wurde beim Klienten ein abweichender Rechnungsempfänger eingetragen. Um beide Klienten auf der Rechnung auszuweisen, darf kein alternativer Rechnungsempfänger eingetragen sein."
                                    persistent-hint
                                    :disabled="!canAddPartnerToBilling"
                                ></v-checkbox>
                                <v-tooltip bottom :open-delay="300" v-if="canAddPartnerToBilling">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon 
                                            v-bind="attrs"
                                            v-on="on"
                                            small
                                        >
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>Wenn diese Option aktiviert ist, werden die Namen beider Klienten (Hauptklient und Partner) beim Platzhalter {empfänger_name} auf der Rechnung eingefügt.</span>
                                </v-tooltip>
                            </div>
                        </div>

                        <!-- Payment Method Section -->
                        <div v-if="canExpandRechnungsdetails" class="mt-6">
                            <p class="text-subtitle-1 font-weight-medium mb-4">
                                Zahlungsmethode
                            </p>
                            <v-tabs v-model="selectedPaymentMethod" class="mt-3 payment-method-tabs" @change="onPaymentMethodChange" grow hide-slider active-class="payment-tab-active">
                                <v-tab>              
                                    <v-icon left>mdi-bank-transfer</v-icon>              
                                    Überweisung
                                </v-tab>
                                <v-tab>                            
                                    <v-icon left>mdi-cash</v-icon>
                                    Bar
                                </v-tab>
                                <v-tab>
                                    <v-icon left>mdi-credit-card</v-icon>
                                    Bankomat
                                </v-tab>
                            </v-tabs>
                            
                            <div class="mt-4">
                                <!-- Überweisung Content -->
                                <div v-if="selectedPaymentMethod === 0">
                                    <v-alert dense outlined type="info" class="mb-4">
                                        <span>Überweisungen werden meist später bezahlt. Du kannst die Rechnung nachträglich als "Bezahlt" markieren.</span>
                                    </v-alert>
                                    <v-checkbox 
                                        v-model="isAlreadyPaid" 
                                        :color="$store.state.theme.green"
                                        label="Rechnung bereits bezahlt?"
                                        class="mt-0"
                                    />
                                    <v-menu v-if="isAlreadyPaid" v-model="menu_paid_transfer" :close-on-content-click="true" open-on-click
                                        offset-y max-width="290px" min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field 
                                                dense 
                                                outlined 
                                                v-model="paidDateText"
                                                label="Bezahlt am" 
                                                prepend-inner-icon="mdi-calendar"
                                                clearable
                                                clear-icon="mdi-close-circle"
                                                @click:clear="clearPaymentDateAndUncheck"
                                                :rules="dateRule"
                                                @input="updateDatePicker"
                                                v-bind="attrs"
                                                v-on="on"
                                                class="mt-1"
                                            />
                                        </template>
                                        <v-date-picker
                                            full-width 
                                            first-day-of-week="1" 
                                            v-model="editedItem.bezahlt" 
                                            no-title 
                                            :color="$store.state.theme.green"
                                            elevation="1"
                                            @input="updateTextField" 
                                            class="mt-2 py-3"
                                        />
                                    </v-menu>
                                </div>
                                
                                <!-- Bar Content -->
                                <div v-if="selectedPaymentMethod === 1">
                                    <v-alert dense outlined type="info" class="mb-4">
                                        <span><strong>Die Rechnung wird als "Bezahlt" markiert.</strong> Barzahlungen werden grundsätzlich sofort bezahlt - das heutige Datum ist bereits vorausgewählt.</span>
                                    </v-alert>
                                    <v-menu v-model="menu_paid_cash" :close-on-content-click="true" open-on-click
                                        offset-y max-width="290px" min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field 
                                                dense 
                                                outlined 
                                                v-model="paidDateText"
                                                label="Bezahlt am" 
                                                prepend-inner-icon="mdi-calendar"
                                                :clearable="false"
                                                :rules="dateRule"
                                                @input="updateDatePicker"
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            full-width 
                                            first-day-of-week="1" 
                                            v-model="editedItem.bezahlt" 
                                            no-title 
                                            :color="$store.state.theme.green"
                                            elevation="1"
                                            @input="updateTextField" 
                                            class="mt-2 py-3"
                                        />
                                    </v-menu>
                                </div>
                                
                                <!-- Bankomat Content -->
                                <div v-if="selectedPaymentMethod === 2">
                                    <v-alert dense outlined type="info" class="mb-4">
                                        <span><strong>Die Rechnung wird als "Bezahlt" markiert.</strong> Bankomatzahlungen werden grundsätzlich sofort bezahlt - das heutige Datum ist bereits vorausgewählt.</span>
                                    </v-alert>
                                    <v-menu v-model="menu_paid_card" :close-on-content-click="true" open-on-click
                                        offset-y max-width="290px" min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field 
                                                dense 
                                                outlined 
                                                v-model="paidDateText"
                                                label="Bezahlt am" 
                                                prepend-inner-icon="mdi-calendar"
                                                :clearable="false"
                                                :rules="dateRule"
                                                @input="updateDatePicker"
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            full-width 
                                            first-day-of-week="1" 
                                            v-model="editedItem.bezahlt" 
                                            no-title 
                                            :color="$store.state.theme.green"
                                            elevation="1"
                                            @input="updateTextField" 
                                            class="mt-2 py-3"
                                        />
                                    </v-menu>
                                </div>
                            </div>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
            </template>

        </v-card-text>

        <v-card-actions class="d-flex justify-end mt-5 px-6 pb-5">
            <v-btn :color="$store.state.theme.primary" text @click="handleClose">
                Abbrechen
            </v-btn>
            <v-btn :color="$store.state.theme.green" outlined @click="save" :loading="updating_invoice" :disabled="!isFormValid">
                Erstellen
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import dayjs from 'dayjs';
import connector from '../helpers/supabase-connector.js';
import invoiceHelper from '../helpers/invoices.js';

export default {
    props: {
        session: Object,
        // Either provide customers list (for dropdown selection) or preSelectedClient (for Klienten.vue)
        customers: {
            type: Array,
            default: () => []
        },
        n_customers: {
            type: Number,
            default: 0
        },
        // Pre-selected client for Klienten.vue context
        preSelectedClient: {
            type: Object,
            default: null
        },
        // Appointments list
        appointments: {
            type: Array,
            default: () => []
        },
        n_appointments: {
            type: Number,
            default: 0
        },
        // Existing invoices for number generation
        invoices: {
            type: Array,
            default: () => []
        },
        // Dialog visibility prop to trigger reset
        dialogVisible: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'invoice-created', 'showError', 'showInfo'],

    data() {
        return {
            updating_invoice: false,
            selected_appointments: [],

            menu: false,
            menu_paid_transfer: false,
            menu_paid_cash: false,
            menu_paid_card: false,
            
            headers_appointments: [
                { text: 'Klient', value: 'name' },
                { text: 'Datum', value: 'datum' },
                { text: 'Dienstleistung', value: 'bezeichnung' },
                { text: 'Preis', value: 'preis' }
            ],

            paidDateText: '',
            
            // Expansion panel states
            rechnungsdetailsPanel: null,
            terminePanel: null,
            
            // Payment method and status
            selectedPaymentMethod: 0, // 0 = Überweisung, 1 = Bar, 2 = Bankomat
            isAlreadyPaid: false,

            dateRule: [
                v => {
                    return true; // Allow empty date for new invoices
                },
                v => {
                    if (!v) return true; // Skip format validation if empty
                    return /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.(\d{4})$/.test(v) || 'Datum im Format TT.MM.JJJJ angeben.';
                },
            ],

            // Initialize editedItem with default values
            editedItem: {
                selected_customer: null,
                datum: new Date().toISOString().split('T')[0],
                nummer: '',
                bezahlt: null,
                bar: null,
                bankomat: null,
                ausweisen_an_beide_klienten: false
            }
        }
    },

    computed: {
        formTitle() {
            if (this.preSelectedClient && this.preSelectedClient.name) {
                return `Neue Rechnung für ${this.preSelectedClient.name}`;
            }
            return 'Neue Rechnung';
        },

        hasSelectedCustomer() {
            return this.preSelectedClient || this.editedItem.selected_customer;
        },

        currentClientName() {
            if (this.preSelectedClient) {
                return this.preSelectedClient.name;
            }
            if (this.editedItem.selected_customer) {
                return this.editedItem.selected_customer.name;
            }
            return '';
        },

        canExpandTermine() {
            return this.hasSelectedCustomer;
        },

        canExpandRechnungsdetails() {
            return this.hasSelectedCustomer && this.selected_appointments.length > 0;
        },

        loadedCustomersAndAppointments() {
            // For pre-selected client mode, always return true
            if (this.preSelectedClient) {
                return true;
            }
            // For dropdown mode, check if data is loaded
            return (this.n_customers === this.customers.length) && (this.n_appointments === this.appointments.length);
        },

        filteredAppointments() {
            if (this.preSelectedClient) {
                // Filter appointments for pre-selected client (from Klienten.vue)
                return this.appointments.filter(appointment => 
                    appointment.fk_klienten_id === this.preSelectedClient.id && 
                    appointment.fk_rechnungs_id === null
                );
            } else if (this.editedItem.selected_customer) {
                // Filter appointments for selected customer (from dropdown)
                if (this.editedItem.selected_customer.type === 'client') {
                    return this.appointments.filter(appointment => 
                        appointment.fk_klienten_id === this.editedItem.selected_customer.fk_klienten_id &&
                        appointment.fk_rechnungs_id === null
                    );
                } else {
                    // Institution - show all unbilled appointments
                    return this.appointments.filter(appointment => appointment.fk_rechnungs_id === null);
                }
            }
            return [];
        },

        hasAvailableAppointments() {
            return this.filteredAppointments.length > 0;
        },

        partnerIdConsistency() {
            const appointmentsSnapshot = [...this.selected_appointments];
            
            const appointmentsWithPartner = appointmentsSnapshot.filter(app => app.fk_partner_id);
            if (!appointmentsWithPartner.length) {
                return { isValid: true, distinctPartnerIds: [], distinctPartnerNames: [] };
            }
            const distinctPartnerIds = [...new Set(appointmentsWithPartner.map(app => app.fk_partner_id))];
            
            const distinctPartnerNames = [...new Set(appointmentsWithPartner.map(app => 
                `${app.partner_vorname || ''} ${app.partner_nachname || ''}`.trim()
            ))].filter(name => name.length > 0);
            
            return {
                isValid: distinctPartnerIds.length <= 1,
                distinctPartnerIds: distinctPartnerIds,
                distinctPartnerNames: distinctPartnerNames
            };
        },

        showInvoiceBothClientsSwitch() {
            if (!this.selected_appointments || this.selected_appointments.length === 0) {
                return false;
            }
            return this.selected_appointments.some(app => app.fk_partner_id);
        },

        canAddPartnerToBilling() {
            // For pre-selected client mode, check client properties
            if (this.preSelectedClient) {
                // If empfänger fields are null/undefined, treat as client being their own recipient (enabled)
                // Otherwise, enable only when client name matches empfänger name (no alternative recipient)
                return (!this.preSelectedClient.empfänger_vorname && !this.preSelectedClient.empfänger_nachname) ||
                       (this.preSelectedClient.vorname === this.preSelectedClient.empfänger_vorname && 
                        this.preSelectedClient.nachname === this.preSelectedClient.empfänger_nachname);
            }
            // For dropdown mode, check selected customer
            if (this.editedItem.selected_customer && this.editedItem.selected_customer.type === 'client') {
                // If empfänger fields are null/undefined, treat as client being their own recipient (enabled)
                // Otherwise, enable only when client name matches empfänger name (no alternative recipient)
                return (!this.editedItem.selected_customer.empfänger_vorname && !this.editedItem.selected_customer.empfänger_nachname) ||
                       (this.editedItem.selected_customer.vorname === this.editedItem.selected_customer.empfänger_vorname && 
                        this.editedItem.selected_customer.nachname === this.editedItem.selected_customer.empfänger_nachname);
            }
            return true;
        },

        invoiceYear() {
            try {
                if (!this.editedItem.nummer) return null;
                const format = invoiceHelper.getInvoiceFormat(this.editedItem.nummer);
                const { year } = invoiceHelper.parseInvoiceNumber(this.editedItem.nummer, format);
                return year;
            } catch (error) {
                return null;
            }
        },

        hasInvoiceYearMismatch() {
            if (!this.invoiceYear || this.hasSelectedInstitution || !this.editedItem.datum) return false;
            const invoiceDateYear = dayjs(this.editedItem.datum).year();
            return this.invoiceYear !== invoiceDateYear;
        },

        hasSelectedInstitution() {
            return this.editedItem.selected_customer && this.editedItem.selected_customer.type === 'institution';
        },

        hintInvoiceNumber() {
            if (this.hasSelectedInstitution) {
                return 'Eine beliebige Rechnungs-Nr. kann vergeben werden.';
            }
            return '';
        },

        computedDateFormatted() {
            return this.formatDate(this.editedItem.datum);
        },

        amountOfSelectedAppointments() {
            try {
                let sum = 0;
                this.selected_appointments.forEach((appointment) => {
                    sum += appointment.preis;
                });
                return sum;
            } catch (error) {
                return 0;
            }
        },

        isFormValid() {
            // Require a customer and at least one appointment
            if (!this.hasSelectedCustomer || this.selected_appointments.length === 0) {
                return false;
            }
            
            // Check partner consistency if the switch is on
            if (this.editedItem.ausweisen_an_beide_klienten && !this.partnerIdConsistency.isValid) {
                return false;
            }
            
            // Require invoice number
            if (!this.editedItem.nummer || this.editedItem.nummer.trim() === '') {
                return false;
            }
            
            return true;
        }
    },

    watch: {
        // Reset dialog state when dialog becomes visible
        dialogVisible: {
            handler(isVisible) {
                if (isVisible) {
                    // Dialog just opened - reset all state
                    this.resetDialogState();
                    // Explicitly open panels if they should be expanded
                    this.$nextTick(() => {
                        if (this.canExpandTermine) {
                            this.terminePanel = 0;
                        }
                        if (this.canExpandRechnungsdetails) {
                            this.rechnungsdetailsPanel = 0;
                        }
                    });
                }
            },
            immediate: false
        },

        // Initialize when pre-selected client is provided
        preSelectedClient: {
            handler(newClient) {
                if (newClient) {
                    this.initializeForPreSelectedClient();
                }
            },
            immediate: true
        },

        // Clear appointments when customer changes (dropdown mode)
        'editedItem.selected_customer': {
            handler(val) {
                this.selected_appointments = [];
            },
            deep: true
        },

        'editedItem.bezahlt': {
            immediate: true,
            handler(newVal) {
                this.paidDateText = this.formatDate(newVal);
            }
        },

        canExpandTermine: {
            handler(canExpand) {
                if (canExpand) {
                    this.terminePanel = 0;
                } else {
                    this.terminePanel = null;
                }
            },
            immediate: true
        },

        canExpandRechnungsdetails: {
            handler(canExpand) {
                if (canExpand) {
                    this.rechnungsdetailsPanel = 0;
                } else {
                    this.rechnungsdetailsPanel = null;
                }
            },
            immediate: true
        },

        isAlreadyPaid: {
            handler(isPaid) {
                if (isPaid && this.selectedPaymentMethod === 0) {
                    this.setTodaysDate();
                } else if (!isPaid) {
                    this.clearPaymentDate();
                }
            }
        }
    },

    mounted() {
        this.initializeInvoiceNumber();
    },

    methods: {
        resetDialogState() {
            // Reset selected customer (only for dropdown mode, not pre-selected client)
            if (!this.preSelectedClient) {
                this.editedItem.selected_customer = null;
            }
            
            // Reset selected appointments
            this.selected_appointments = [];
            
            // Reset payment method state
            this.selectedPaymentMethod = 0;
            this.isAlreadyPaid = false;
            this.paidDateText = '';
            
            // Reset form fields
            this.editedItem.bezahlt = null;
            this.editedItem.bar = null;
            this.editedItem.bankomat = null;
            this.editedItem.ausweisen_an_beide_klienten = false;
            
            // Reset expansion panel states
            this.rechnungsdetailsPanel = null;
            this.terminePanel = null;
            
            // Reset date to today
            this.editedItem.datum = new Date().toISOString().split('T')[0];
            
            // Regenerate invoice number to get the latest
            this.initializeInvoiceNumber();
        },

        initializeInvoiceNumber() {
            try {
                // Use the invoices prop like Rechnungen.vue does
                const invoices = this.invoices || [];

                if (this.$store.state.client.rechnungsnummer_format) {
                    if (!invoiceHelper.isValidFormat(this.$store.state.client.rechnungsnummer_format)) {
                        this.$emit('showError', { message: 'Das Rechnungsnummer-Format ist nicht korrekt. Bitte überprüfe das Format in den Einstellungen.' });
                        return;
                    }
                    this.editedItem.nummer = invoiceHelper.getNextInvoiceNumber(invoices, this.$store.state.client.rechnungsnummer_format);
                } else if (!this.$store.state.client.eigenes_schema) {
                    this.editedItem.nummer = invoiceHelper.getNextInvoiceNumber(invoices, '{NR}/{JAHR}');
                }
            } catch (error) {
                this.$emit('showError', { message: error.message, timeout: 8000 });
            }
        },

        initializeForPreSelectedClient() {
            // Initialize payment state
            this.selectedPaymentMethod = 0;
            this.isAlreadyPaid = false;
            this.paidDateText = '';
            
            // Clear selected appointments
            this.selected_appointments = [];
            
            // Initialize editedItem for pre-selected client context
            this.editedItem.selected_customer = null; // Will use preSelectedClient instead
            this.editedItem.ausweisen_an_beide_klienten = false;
        },

        onPaymentMethodChange(tabIndex) {
            if (tabIndex === 1) { // Bar (Cash)
                this.setTodaysDate();
                this.editedItem.bar = true;
            } else if (tabIndex === 2) { // Bankomat (Card)
                this.setTodaysDate();
                this.editedItem.bar = true;
                this.editedItem.bankomat = true;
            } else if (tabIndex === 0) { // Überweisung (Transfer)
                this.isAlreadyPaid = false;
                this.clearPaymentDate();
                this.editedItem.bar = null;
                this.editedItem.bankomat = null;
            }
        },

        setTodaysDate() {
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];
            this.editedItem.bezahlt = formattedDate;
            this.paidDateText = this.formatDate(formattedDate);
        },

        updateInvoiceNumberToCurrentYear() {
            const result = invoiceHelper.updateInvoiceNumberToCurrentYear(
                this.editedItem.nummer, 
                this.editedItem.datum
            );
            
            if (result.status === "success") {
                this.editedItem.nummer = result.invoiceNumber;
            } else {
                this.$emit('showError', { 
                    message: result.message, 
                    error: result.error 
                });
            }
        },

        formatInvoiceDateYear(dateStr) {
            if (!dateStr) return null;
            return dayjs(dateStr).format('YYYY');
        },

        toggleSelection(item) {
            const index = this.selected_appointments.findIndex(selected => selected.id === item.id);
            if (index === -1) {
                this.selected_appointments.push(item);
            } else {
                this.selected_appointments.splice(index, 1);
            }
        },

        clearPaymentDate() {
            this.editedItem.bezahlt = null;
            this.paidDateText = '';
            if (this.editedItem.bar) {
                this.editedItem.bar = null;
            }
            if (this.editedItem.bankomat) {
                this.editedItem.bankomat = null;
            }
        },

        clearPaymentDateAndUncheck() {
            this.clearPaymentDate();
            this.isAlreadyPaid = false;
        },

        handleClose() {
            // Reset all dialog state when closing
            this.resetDialogState();
            this.$emit('close');
        },

        updateTextField(newDate) {
            this.paidDateText = this.formatDate(newDate);
        },

        updateDatePicker() {
            if (this.paidDateText === '') {
                this.clearPaymentDate();
            } else {
                const parsedDate = dayjs(this.paidDateText, 'DD.MM.YYYY', true);
                if (parsedDate.isValid()) {
                    this.editedItem.bezahlt = parsedDate.format('YYYY-MM-DD');
                }
            }
        },
        
        formatDate(date) {
            if (!date) return null;
            const [year, month, day] = date.split('T')[0].split('-');
            return `${day}.${month}.${year}`;
        },

        isAfterNow(dateString) {
            const parsed_date = dayjs.tz(dateString, 'YYYY-MM-DDTHH:mm:ss', 'Europe/Vienna');
            const now = dayjs().tz('Europe/Vienna');
            return parsed_date.isAfter(now);
        },

        async save() {
            this.updating_invoice = true;

            let invoice = JSON.parse(JSON.stringify(this.editedItem));

            // Set client/institution IDs based on context
            if (this.preSelectedClient) {
                // Pre-selected client mode (from Klienten.vue)
                invoice.fk_klienten_id = this.preSelectedClient.id;
                invoice.fk_institution_id = null;
            } else if (invoice.selected_customer) {
                // Dropdown selection mode (from Rechnungen.vue)
                if (invoice.selected_customer.type === 'client') {
                    invoice.fk_klienten_id = invoice.selected_customer.fk_klienten_id;
                    invoice.fk_institution_id = null;
                } else {
                    invoice.fk_institution_id = invoice.selected_customer.fk_institution_id;
                    invoice.fk_klienten_id = null;
                }
            }

            // Clean up invoice object
            delete invoice.id;
            delete invoice.selected_customer;
            delete invoice.nachname;
            delete invoice.vorname;
            delete invoice.rechnungs_betrag;
            delete invoice.anzahl_termine;
            
            // Set required fields
            invoice.uid = this.session.user.id;
            invoice.datum = this.editedItem.datum.split('T')[0];

            // Validate invoice number
            if (invoice.nummer === '' || invoice.nummer === null) {
                this.$emit('showError', { 'message': "Eine Rechungsnummer muss vergeben werden." });
                this.updating_invoice = false;
                return;
            }

            // Validate invoice number format (skip for institutions)
            if (!this.hasSelectedInstitution) {
                if (this.$store.state.client.rechnungsnummer_format) {
                    const invoice_regex = invoiceHelper.generateInvoiceRegex(this.$store.state.client.rechnungsnummer_format);
                    if (!invoice_regex.test(invoice.nummer)) {
                        this.$emit('showError', { 'message': "Die Rechungsnummer muss folgendes Format haben: " + this.$store.state.client.rechnungsnummer_format });
                        this.updating_invoice = false;
                        return;
                    }
                } else if (!this.$store.state.client.eigenes_schema) {
                    const invoice_regex = invoiceHelper.generateInvoiceRegex('{NR}/{JAHR}');
                    if (!invoice_regex.test(invoice.nummer)) {
                        this.$emit('showError', { 'message': "Die Rechungsnummer muss folgendes Format haben: NR/JJJJ (zb. 1/2022)." });
                        this.updating_invoice = false;
                        return;
                    }
                }
            }

            // Validate appointments selection
            let updated_appointments = this.selected_appointments.map(appointment => appointment.id);
            if (updated_appointments.length === 0) {
                this.$emit('showError', { 'message': "Mindestens ein Termin muss für eine Rechnung ausgewählt werden." });
                this.updating_invoice = false;
                return;
            }

            // Add partner billing fields
            invoice.ausweisen_an_beide_klienten = this.editedItem.ausweisen_an_beide_klienten;
            invoice.fk_partner_id = null;
            if (this.editedItem.ausweisen_an_beide_klienten && this.partnerIdConsistency.isValid && this.partnerIdConsistency.distinctPartnerIds.length === 1) {
                invoice.fk_partner_id = this.partnerIdConsistency.distinctPartnerIds[0];
            } else if (this.editedItem.ausweisen_an_beide_klienten && !this.partnerIdConsistency.isValid) {
                this.$emit('showError', { 'message': "Rechnung kann nicht erstellt werden. Es wurde mehr als ein Partner ausgewählt." });
                this.updating_invoice = false;
                return;
            }

            // Create invoice
            let invoice_id = await connector.createInvoice(this, invoice, updated_appointments);
            if (!invoice_id) {
                this.updating_invoice = false;
                return;
            }

            // Handle payment status based on selected method
            if (this.selectedPaymentMethod === 1 || this.selectedPaymentMethod === 2 || (this.selectedPaymentMethod === 0 && this.isAlreadyPaid)) {
                const paymentUpdate = {
                    bezahlt: this.editedItem.bezahlt,
                    bar: this.selectedPaymentMethod === 1 || this.selectedPaymentMethod === 2 ? true : null,
                    bankomat: this.selectedPaymentMethod === 2 ? true : null
                };
                
                let updated = await connector.update(this, 'rechnungen', paymentUpdate, invoice_id);
                if (!updated) {
                    this.$emit('showError', { 'message': "Rechnung wurde erstellt, aber Zahlungsstatus konnte nicht gesetzt werden." });
                    this.updating_invoice = false;
                    return;
                }
            }

            this.$emit('invoice-created', invoice_id);
            this.updating_invoice = false;
            this.$emit('showInfo', {
                message: 'Die Rechnung wurde erfolgreich erstellt.',
                timeout: 5000
            });
            this.handleClose();
        },
    }
}
</script>

<style scoped>
.v-data-table >>> .no-hover:hover {
  background-color: transparent !important;
  cursor: default;
}
.clickable-rows ::v-deep tbody tr {
  cursor: pointer;
}
.flat-expansion-panels ::v-deep .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-bottom: 8px;
}
.flat-expansion-panels ::v-deep .v-expansion-panel:last-child {
  margin-bottom: 0;
}
.payment-method-tabs ::v-deep .v-tabs-bar {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.payment-method-tabs ::v-deep .v-tab:not(:last-child) {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.payment-method-tabs ::v-deep .payment-tab-active {
  background-color: hsl(210, 15%, 90%);
  color: hsl(210, 15%, 20%);
}
.payment-method-tabs ::v-deep .v-tab:hover:not(.payment-tab-active) {
  background-color: hsl(210, 10%, 95%);
}
</style>